import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Plus } from 'lucide-react';
import { RootState, AppDispatch } from '../../redux/store';
import {
  fetchImagePromptTemplates,
  createImagePromptTemplate,
  updateImagePromptTemplate,
  deleteImagePromptTemplate,
  activateImagePromptTemplate
} from '../../redux/admin/adminSlice';
import {
  ImagePromptTemplate,
  ImagePromptTemplateCreateRequest,
  ImagePromptTemplateUpdateRequest
} from '../../services/api/imagePromptTemplateService';
import ImagePromptTemplateEditModal from './ImagePromptTemplateEditModal';
import { TaskyAlert, TaskyButton, TaskySpinner, useConfirm } from '../tasky';
import { useToast } from '../../contexts/ToastContext';

const ImagePromptTemplateList: React.FC = () => {
  const confirm = useConfirm();
  const { showToast } = useToast();
  const dispatch = useDispatch<AppDispatch>();
  const { imagePromptTemplates, isLoading } = useSelector(
    (state: RootState) => state.admin
  );

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<ImagePromptTemplate | null>(null);

  useEffect(() => {
    dispatch(fetchImagePromptTemplates());
  }, [dispatch]);

  const handleCreateTemplate = () => {
    setEditingTemplate(null);
    setIsModalOpen(true);
  };

  const handleEditTemplate = (template: ImagePromptTemplate) => {
    setEditingTemplate(template);
    setIsModalOpen(true);
  };

  const handleSubmitTemplate = async (
    data: ImagePromptTemplateCreateRequest | ImagePromptTemplateUpdateRequest
  ) => {
    try {
      if (editingTemplate) {
        await dispatch(
          updateImagePromptTemplate({ id: editingTemplate.id, templateData: data })
        ).unwrap();
      } else {
        await dispatch(createImagePromptTemplate(data)).unwrap();
      }
      setIsModalOpen(false);
      setEditingTemplate(null);
    } catch (error) {
      console.error('Failed to save template:', error);
    }
  };

  const handleActivateTemplate = async (templateId: number) => {
    try {
      await dispatch(activateImagePromptTemplate(templateId)).unwrap();
    } catch (error) {
      console.error('Failed to activate template:', error);
    }
  };

  const handleDeleteTemplate = async (templateId: number, templateName: string) => {
    if (await confirm({ title: `プロンプトテンプレート「${templateName}」を削除してもよろしいですか？`, tone: 'destructive', confirmText: '削除する' })) {
      try {
        await dispatch(deleteImagePromptTemplate(templateId)).unwrap();
      } catch (error) {
        console.error('Failed to delete template:', error);
        showToast('テンプレートの削除に失敗しました。有効なテンプレートは削除できません。', 'error');
      }
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingTemplate(null);
  };

  // Group templates by type
  const singleTemplates = imagePromptTemplates.filter(t => t.type === 'single');
  const variationsTemplates = imagePromptTemplates.filter(t => t.type === 'variations');

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ja-JP', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const renderTemplateCard = (template: ImagePromptTemplate) => (
    <div
      key={template.id}
      className={`rounded-tasky-md border p-4 mb-3 transition-colors ${
        template.is_active
          ? 'bg-primary/5 border-primary'
          : 'bg-white border-warm-border hover:border-gray-300 hover:shadow-sm'
      }`}
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 text-sm font-semibold text-dark">
            {template.name}
            {template.is_active && (
              <span className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium bg-success text-white">
                有効
              </span>
            )}
          </div>
          <div className="text-xs text-gray-400 mt-1">更新: {formatDate(template.updated_at)}</div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {!template.is_active && (
            <button
              type="button"
              className="px-2.5 py-1 text-xs font-semibold bg-success/10 border border-success/40 text-success rounded-tasky-xs hover:bg-success/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={() => handleActivateTemplate(template.id)}
              disabled={isLoading}
            >
              有効化
            </button>
          )}
          <button
            type="button"
            className="px-2.5 py-1 text-xs font-semibold bg-white border border-warm-border text-gray-700 rounded-tasky-xs hover:bg-warm-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={() => handleEditTemplate(template)}
            disabled={isLoading}
          >
            編集
          </button>
          {!template.is_active && (
            <button
              type="button"
              className="px-2.5 py-1 text-xs font-semibold bg-red-50 border border-red-200 text-red-600 rounded-tasky-xs hover:bg-red-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={() => handleDeleteTemplate(template.id, template.name)}
              disabled={isLoading}
            >
              削除
            </button>
          )}
        </div>
      </div>
      <div className="text-xs text-gray-500 bg-warm-surface border border-warm-border rounded-tasky-sm px-3 py-2 max-h-20 overflow-hidden whitespace-pre-wrap leading-relaxed">
        {template.content.length > 200
          ? template.content.substring(0, 200) + '...'
          : template.content}
      </div>
    </div>
  );

  if (isLoading && imagePromptTemplates.length === 0) {
    return (
      <div className="flex items-center justify-center gap-3 py-12 text-sm text-gray-500">
        <TaskySpinner size="md" />
        読み込み中...
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-base font-semibold text-dark">プロンプトテンプレート一覧</h2>
        <TaskyButton size="sm" onClick={handleCreateTemplate} disabled={isLoading}>
          <Plus />
          新規テンプレート作成
        </TaskyButton>
      </div>

      <div className="mb-8">
        <h3 className="text-sm font-semibold text-dark border-b border-warm-border pb-2 mb-4">
          単一生成用 (Single)
        </h3>
        {singleTemplates.length > 0 ? (
          singleTemplates.map(renderTemplateCard)
        ) : (
          <p className="text-sm text-gray-500 text-center py-8">単一生成用のテンプレートがありません。</p>
        )}
      </div>

      <div className="mb-8">
        <h3 className="text-sm font-semibold text-dark border-b border-warm-border pb-2 mb-4">
          バリエーション生成用 (Variations)
        </h3>
        {variationsTemplates.length > 0 ? (
          variationsTemplates.map(renderTemplateCard)
        ) : (
          <p className="text-sm text-gray-500 text-center py-8">バリエーション生成用のテンプレートがありません。</p>
        )}
      </div>

      <TaskyAlert tone="warning" title="利用可能な変数プレースホルダー" className="mt-6">
        <ul className="list-disc pl-5 space-y-1">
          <li>
            <code className="bg-warning/20 px-1 py-0.5 rounded font-mono text-xs">{'{targetCharCount}'}</code> - 目標文字数（プロンプトの長さ目安）
          </li>
          <li>
            <code className="bg-warning/20 px-1 py-0.5 rounded font-mono text-xs">{'{maxCharCount}'}</code> - 最大文字数
          </li>
          <li>
            <code className="bg-warning/20 px-1 py-0.5 rounded font-mono text-xs">{'{count}'}</code> - バリエーション数（variations用のみ）
          </li>
        </ul>
      </TaskyAlert>

      <ImagePromptTemplateEditModal
        isOpen={isModalOpen}
        template={editingTemplate}
        onSubmit={handleSubmitTemplate}
        onClose={handleCloseModal}
        isLoading={isLoading}
      />
    </div>
  );
};

export default ImagePromptTemplateList;
